package com.example.socialmediaapp.ui.screens

import androidx.compose.animation.core.*
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.example.socialmediaapp.network.CallRepository
import com.example.socialmediaapp.openVideoCall

// CallScreen: fetches the Jitsi room URL, then hands off to the native Jitsi SDK.
// On Android: launches JitsiMeetActivity (full native call UI).
// On Web: opens the Jitsi room in a new tab.

@Composable
fun CallScreen(
    chatId: String,
    chatName: String = "Unknown",
    isVideoCall: Boolean = false,
    callRepository: CallRepository? = null,
    onEnd: () -> Unit = {}
) {
    var status by remember { mutableStateOf("Connecting...") }
    var isError by remember { mutableStateOf(false) }

    val infiniteTransition = rememberInfiniteTransition(label = "pulse")
    val pulseAlpha by infiniteTransition.animateFloat(
        initialValue = 0.4f, targetValue = 1f,
        animationSpec = infiniteRepeatable(tween(900), RepeatMode.Reverse),
        label = "pulse"
    )

    LaunchedEffect(chatId) {
        if (callRepository == null) {
            // No repo configured — navigate back
            onEnd()
            return@LaunchedEffect
        }
        callRepository.getRoom(chatId).fold(
            onSuccess = { response ->
                if (response.roomUrl.isBlank()) {
                    isError = true
                    status = "VoIP not configured on server.\nAdd JITSI_DOMAIN to .env and restart."
                } else {
                    status = "Launching call..."
                    // Hand off to platform-specific Jitsi launcher
                    openVideoCall(response.roomUrl, isVideoCall)
                    // Return to app after launching (Jitsi runs in its own Activity/window)
                    onEnd()
                }
            },
            onFailure = {
                isError = true
                status = "Could not connect: ${it.message}"
            }
        )
    }

    // Shown briefly while fetching room URL
    Box(
        modifier = Modifier.fillMaxSize().background(Color(0xFF0D0D0D)),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(24.dp),
            modifier = Modifier.padding(32.dp)
        ) {
            Box(
                modifier = Modifier.size(100.dp).background(Color(0xFF2A2A2A), CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    chatName.firstOrNull()?.uppercase() ?: "?",
                    style = MaterialTheme.typography.displayMedium,
                    color = Color.White,
                    fontWeight = FontWeight.Bold
                )
            }

            Text(chatName, style = MaterialTheme.typography.headlineSmall, color = Color.White, fontWeight = FontWeight.Bold)

            Text(
                status,
                style = MaterialTheme.typography.bodyLarge,
                color = if (isError) Color(0xFFFF5555) else Color.White.copy(alpha = pulseAlpha)
            )

            if (isError) {
                Spacer(Modifier.height(8.dp))
                Button(
                    onClick = onEnd,
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2A2A2A))
                ) {
                    Text("Go back", color = Color.White)
                }
            }
        }
    }
}
